// @flow
import * as React from 'react';
import AExperimentsClient from 'app/common/lib/ExperimentsClient/AExperimentsClient';
import PropTypes from 'prop-types';
import QuickPerf from 'app/common/lib/logging/QuickPerf';
import type { ClientContext } from 'app/common/lib/context';
import urlUtil from 'app/common/lib/urlUtil';
import {
  RequestContextPropType,
  RequestContextProvider,
} from 'app/common/react/components/RequestContext';
import { RequestContextFactory, type RequestContext } from 'app/common/lib/RequestContext';

type Props = {
  children: React.Node,
  context: ClientContext,
  experiments: AExperimentsClient,
  seoExperiments: AExperimentsClient,
  seoUnauthExperiments: AExperimentsClient,
};

type State = {
  isFromNavigate: boolean,
  isFBBrowser: boolean,
  searchType: string,
  searchQuery: string,
  searchReferrer: string,
};

export const SearchType = {
  IMAGES: 'images',
  UNKNOWN: 'unknown',
  WEB: 'web',
};

/**
 * Exposes unauth related context fields, as provided via the django context
 */
export default class UnauthContext extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const [searchType, searchQuery, searchReferrer] = this.getSearchType();
    const isFBBrowser = this.isFBBrowser();
    const isFromNavigate = this.isFromNavigate();
    this.state = {
      isFromNavigate,
      isFBBrowser,
      searchType,
      searchQuery,
      searchReferrer,
    };
  }

  getChildContext() {
    const { context } = this.props;
    return {
      appVersion: context.app_version,
      browserName: context.browser_name,
      cookies: context.cookies,
      country: context.country,
      currentUrl: context.current_url,
      deepLink: context.deep_link,
      fullPath: context.full_path,
      httpReferrer: context.http_referrer,
      isFromNavigate: this.state.isFromNavigate,
      isAmp: context.is_amp,
      isAuthenticated: context.is_authenticated,
      isBot: context.is_bot === 'true',
      isFBBrowser: this.state.isFBBrowser,
      isNotFromSearchEngine: ['facebook', 'partner'].includes(this.state.searchReferrer),
      language: context.language,
      locale: context.locale,
      origin: context.origin,
      searchQuery: this.state.searchQuery,
      searchReferrer: this.state.searchReferrer,
      searchType: this.state.searchType,
      unauthId: context.unauth_id,
      requestContext: this.getRequestContext(this.props),
    };
  }

  componentDidMount() {
    const requestContext = this.getRequestContext(this.props);
    if (QuickPerf.enabled(requestContext)) {
      window.requestAnimationFrame(() => {
        QuickPerf.logFromClient('unauthContext', this.props.context);
      });
    }
  }

  getRequestContext(props: Props): RequestContext {
    return RequestContextFactory.fromDesktopContextProps(props);
  }

  getSearchType() {
    // regex is based off of referred_traffic_mapper
    const { context } = this.props;
    let searchType = SearchType.UNKNOWN;
    let searchQuery = '';
    let searchReferrer = '';
    const referrer = context.http_referrer;
    const match =
      referrer &&
      referrer.match(/^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)(\/[^?#]*)(\?[^#]*|)(#.*|)$/);
    if (match && match.length >= 2) {
      const referrer_host = match[2];
      if (referrer_host.search('^.*(google[.].+)$') > -1) {
        searchReferrer = 'google';
        if (
          referrer.search(
            /((images\.google)|(\/images|\/imgres).*\?.+)|(\?.*(source=images|tbm=isch))/,
          ) > -1
        ) {
          searchType = SearchType.IMAGES;
          const query = urlUtil.getQueryStringParams(referrer);
          if (query.q && query.q.length > 0) {
            searchQuery = query.q;
          }
        } else if (referrer.search(/search|(\?.*(source=web))/) > -1) {
          searchType = SearchType.WEB;
        }
      } else if (referrer_host.search('^.*(bing[.].+)$') > -1) {
        searchReferrer = 'bing';
        if (referrer.search('bing.[a-z]+((/images/search)|(.*?.*(source=images)))') > -1) {
          searchType = SearchType.IMAGES;
          const query = urlUtil.getQueryStringParams(referrer);
          if (query.pq && query.pq.length > 0) {
            searchQuery = query.pq;
          }
        } else if (referrer.search('(bing.[a-z]+(/search|(.*?.*(source=search))))') > -1) {
          searchType = SearchType.WEB;
        }
      } else if (referrer_host.search('^.*(yahoo[.].+)$') > -1) {
        searchReferrer = 'yahoo';
        if (referrer.search('images.search.yahoo.[a-z]+((/search/images)|(.*?.*))') > -1) {
          searchType = SearchType.IMAGES;
          const query = urlUtil.getQueryStringParams(referrer);
          if (query.p && query.p.length > 0) {
            searchQuery = query.p;
          }
        } else if (referrer.search('(search.yahoo.[a-z]+(/search|(.*?.*)))') > -1) {
          searchType = SearchType.WEB;
        }
      } else if (referrer_host.search('^.*(facebook[.].+)$') > -1) {
        searchReferrer = 'facebook';
      } else if (referrer_host.search('.cdn.ampproject.org$') > -1) {
        searchReferrer = 'amp';
      } else {
        searchReferrer = 'partner';
      }
    }
    return [searchType, searchQuery, searchReferrer];
  }

  isFBBrowser() {
    const { context } = this.props;
    return context.user_agent.indexOf('FBAN') > -1 || context.user_agent.indexOf('FBAV') > -1;
  }

  isFromNavigate() {
    const { context } = this.props;
    if (!context.http_referrer) {
      return false;
    }
    const urlSplit = context.http_referrer.split('/');
    return (
      urlSplit.length >= 3 &&
      (urlSplit[2].indexOf('.pinterest.') > -1 || urlSplit[2].indexOf('.pinterdev.') > -1)
    );
  }

  render() {
    return (
      <RequestContextProvider value={this.getRequestContext(this.props)}>
        {this.props.children}
      </RequestContextProvider>
    );
  }
}

UnauthContext.childContextTypes = {
  appVersion: PropTypes.string.isRequired,
  browserName: PropTypes.string,
  cookies: PropTypes.object.isRequired,
  currentUrl: PropTypes.string.isRequired,
  country: PropTypes.string,
  deepLink: PropTypes.string,
  fullPath: PropTypes.string.isRequired,
  httpReferrer: PropTypes.string,
  isFromNavigate: PropTypes.bool.isRequired,
  isAmp: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isBot: PropTypes.bool.isRequired,
  isFBBrowser: PropTypes.bool.isRequired,
  isNotFromSearchEngine: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  origin: PropTypes.string.isRequired,
  searchQuery: PropTypes.string.isRequired,
  searchReferrer: PropTypes.string.isRequired,
  searchType: PropTypes.string.isRequired,
  unauthId: PropTypes.string.isRequired,
  requestContext: RequestContextPropType,
};

UnauthContext.propTypes = {
  children: PropTypes.node.isRequired,
  context: PropTypes.shape({
    app_version: PropTypes.string.isRequired,
    cookies: PropTypes.object.isRequired,
    country: PropTypes.string,
    current_url: PropTypes.string,
    deep_link: PropTypes.string,
    is_amp: PropTypes.bool.isRequired,
    is_authenticated: PropTypes.bool.isRequired,
    is_bot: PropTypes.oneOf(['true', 'false']),
    language: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    http_referrer: PropTypes.string.isRequired,
    unauth_id: PropTypes.string.isRequired,
  }).isRequired,
  experiments: PropTypes.instanceOf(AExperimentsClient).isRequired,
  seoExperiments: PropTypes.instanceOf(AExperimentsClient).isRequired,
  seoUnauthExperiments: PropTypes.instanceOf(AExperimentsClient).isRequired,
};
